export namespace GasPositioning {
  /** Направления в которых может быть выровнен элемент */
  export enum Direction {
    Top = 'top',
    TopRight = 'topRight',
    TopLeft = 'topLeft',
    Bottom = 'bottom',
    BottomRight = 'bottomRight',
    BottomLeft = 'bottomLeft',
    Left = 'left',
    Right = 'right',
  }

  export type DirectionType = Direction.Top
    | Direction.TopRight
    | Direction.TopLeft
    | Direction.Bottom
    | Direction.BottomRight
    | Direction.BottomLeft
    | Direction.Left
    | Direction.Right
    | string;

  /** Тип выравнивания, auto - выравнивает если не влезает элемент, fixed - всегда статично */
  export type AlignType = 'auto' | 'fixed';

  /** Список необходимого для выравнивания */
  export interface PositionSetup {
    popup: HTMLElement;
    parent: HTMLElement;
    offset: number;
    arrowWidth?: number;
    alignType?: AlignType;
    preferablePositions: Direction[] | string[];
    after: (direction: Direction | string) => void;
  }

  function isHasBottomPlace(tooltipRect: ClientRect, parentRect: ClientRect, offset: number): boolean {
    return (parentRect.bottom + offset + tooltipRect.height) < window.innerHeight;
  }

  function isHasTopPlace(tooltipRect: ClientRect, parentRect: ClientRect, offset: number): boolean {
    return parentRect.top > (tooltipRect.height + offset);
  }

  function isHasTopRightPlace(tooltipRect: ClientRect, parentRect: ClientRect, offset: number): boolean {
    return isHasTopPlace(tooltipRect, parentRect, offset)
      && isHasRightPlaceAngle(tooltipRect, parentRect, offset);
  }

  function isHasTopLeftPlace(tooltipRect: ClientRect, parentRect: ClientRect, offset: number): boolean {
    return isHasTopPlace(tooltipRect, parentRect, offset)
      && isHasLeftPlaceAngle(tooltipRect, parentRect, offset);
  }

  function isHasRightPlaceAngle(tooltipRect: ClientRect, parentRect: ClientRect, offset: number): boolean {
    return (parentRect.right + tooltipRect.width + offset) < window.innerWidth;
  }

  function isHasLeftPlaceAngle(tooltipRect: ClientRect, parentRect: ClientRect, offset: number): boolean {
    return (parentRect.left - parentRect.width / 2 - tooltipRect.width + offset) > 0;
  }

  function isHasLeftPlace(tooltipRect: ClientRect, parentRect: ClientRect, offset: number): boolean {
    return parentRect.left > (tooltipRect.width + offset);
  }

  function isHasRightPlace(tooltipRect: ClientRect, parentRect: ClientRect, offset: number): boolean {
    return (parentRect.right + tooltipRect.width + offset) < window.innerWidth;
  }

  function isHasCenterLeftPlace(tooltipRect: ClientRect, parentRect: ClientRect, offset: number): boolean {
    return parentRect.left + (parentRect.width / 2) - (tooltipRect.width / 2) - offset > 0;
  }

  function isHasCenterRightPlace(tooltipRect: ClientRect, parentRect: ClientRect, offset: number): boolean {
    return parentRect.right - (parentRect.width / 2) + (tooltipRect.width / 2) + offset < window.innerWidth;
  }

  /** Набор методов для определения направления выравнивания */
  export const DIRECTION_ADJUSTMENT = {
    bottom(tooltipRect: ClientRect, parentRect: ClientRect, offset: number): boolean {
      return isHasBottomPlace(tooltipRect, parentRect, offset)
        && isHasCenterLeftPlace(tooltipRect, parentRect, offset)
        && isHasCenterRightPlace(tooltipRect, parentRect, offset);
    },

    top(tooltipRect: ClientRect, parentRect: ClientRect, offset: number): boolean {
      return isHasTopPlace(tooltipRect, parentRect, offset)
        && isHasCenterLeftPlace(tooltipRect, parentRect, offset)
        && isHasCenterRightPlace(tooltipRect, parentRect, offset);
    },

    bottomRight(tooltipRect: ClientRect, parentRect: ClientRect, offset: number): boolean {
      return isHasBottomPlace(tooltipRect, parentRect, offset)
        && isHasRightPlaceAngle(tooltipRect, parentRect, offset);
    },

    bottomLeft(tooltipRect: ClientRect, parentRect: ClientRect, offset: number): boolean {
      return isHasBottomPlace(tooltipRect, parentRect, offset)
        && isHasLeftPlaceAngle(tooltipRect, parentRect, offset);
    },

    topRight(tooltipRect: ClientRect, parentRect: ClientRect, offset: number): boolean {
      return isHasTopRightPlace(tooltipRect, parentRect, offset);
    },

    topLeft(tooltipRect: ClientRect, parentRect: ClientRect, offset: number): boolean {
      return isHasTopLeftPlace(tooltipRect, parentRect, offset);
    },

    right(tooltipRect: ClientRect, parentRect: ClientRect, offset: number): boolean {
      return isHasRightPlace(tooltipRect, parentRect, offset);
    },

    left(tooltipRect: ClientRect, parentRect: ClientRect, offset: number): boolean {
      return isHasLeftPlace(tooltipRect, parentRect, offset);
    },
  };

  /** Константа сводит к минимуму вероятность появления зазора между декоративным псевдоэлементом и блоком */
  const TRIM_SIZE = 0;

  /** Обрабатывает исключение, когда элемент находится слева/справа и его нужно сдвинуть по вертикали */
  const initTopPositionForRightLeftCase = function(positionData: GasPositioning.PositionSetup): void {
    const { popup, parent, offset, arrowWidth } = positionData;

    const tooltipRect: ClientRect = popup.getBoundingClientRect();
    const parentRect: ClientRect = parent.getBoundingClientRect();

    const halfedTooltipHeight = tooltipRect.height / 2;
    const halfedParentHeight = parentRect.height / 2;

    const hasPlaceInBottom: boolean = isHasBottomPlace(tooltipRect, parentRect, offset);
    const hasPlaceInTop: boolean = isHasTopPlace(tooltipRect, parentRect, offset);
    const additionalOffset = (arrowWidth / 2 + TRIM_SIZE) + offset;

    /** Есть место и сверху и снизу, отображаем по середине */
    if (hasPlaceInTop && hasPlaceInBottom) {
      popup.style.top = `${parentRect.bottom - halfedParentHeight - halfedTooltipHeight + offset}px`;
    }
    /** Есть место сверху, но нет снизу */
    else if (hasPlaceInTop && !hasPlaceInBottom) {
      popup.style.top = `${parentRect.top - tooltipRect.height - additionalOffset}px`;
    }
    /** Есть место снизу, но нет сверху */
    else if (hasPlaceInBottom && !hasPlaceInTop) {
      popup.style.top = `${parentRect.bottom + additionalOffset}px`;
    }
    /** Нет места не сверху, ни снизу */
    else if (!hasPlaceInBottom && !hasPlaceInTop) {
      const neededHeight = window.innerHeight - (parentRect.bottom + additionalOffset + tooltipRect.height);
      popup.style.top = `${parentRect.bottom - halfedParentHeight - halfedTooltipHeight - neededHeight}px`;
    }

    popup.style.bottom = 'inherit';
  };

  /** Набор методов для выравнивания по соответствующему направлению */
  export const ALIGN_BY = {
    bottom(positionData: GasPositioning.PositionSetup): void {
      const { popup, parent, offset, arrowWidth } = positionData;
      const additionalOffset = (arrowWidth / 2 + TRIM_SIZE) + offset;

      const tooltipRect: ClientRect = popup.getBoundingClientRect();
      const parentRect: ClientRect = parent.getBoundingClientRect();

      popup.style.left = `${parentRect.left + (parentRect.width / 2) - (tooltipRect.width / 2)}px`;
      popup.style.top = `${parentRect.bottom + additionalOffset}px`;
      popup.style.bottom = 'inherit';
    },

    bottomRight(positionData: GasPositioning.PositionSetup): void {
      const { popup, parent, offset, arrowWidth } = positionData;
      const additionalOffset = (arrowWidth / 2) + TRIM_SIZE + offset;

      const parentRect: ClientRect = parent.getBoundingClientRect();

      popup.style.left = `${parentRect.left}px`;
      popup.style.top = `${parentRect.bottom + additionalOffset}px`;
      popup.style.bottom = 'inherit';
    },

    bottomLeft(positionData: GasPositioning.PositionSetup): void {
      const { popup, parent, offset, arrowWidth } = positionData;
      const additionalOffset = (arrowWidth / 2 + TRIM_SIZE) + offset;

      const tooltipRect: ClientRect = popup.getBoundingClientRect();
      const parentRect: ClientRect = parent.getBoundingClientRect();

      popup.style.left = `${parentRect.right - tooltipRect.width}px`;
      popup.style.top = `${parentRect.bottom + additionalOffset}px`;
      popup.style.bottom = 'inherit';
    },

    top(positionData: GasPositioning.PositionSetup): void {
      const { popup, parent, offset, arrowWidth } = positionData;
      const additionalOffset = (arrowWidth / 2 + TRIM_SIZE) + offset;

      const tooltipRect: ClientRect = popup.getBoundingClientRect();
      const parentRect: ClientRect = parent.getBoundingClientRect();

      popup.style.left = `${parentRect.left + (parentRect.width / 2) - (tooltipRect.width / 2)}px`;
      popup.style.top = `${parentRect.top - tooltipRect.height - additionalOffset}px`;
      popup.style.bottom = 'inherit';
    },

    topRight(positionData: GasPositioning.PositionSetup): void {
      const { popup, parent, offset, arrowWidth } = positionData;
      const additionalOffset = (arrowWidth / 2 + TRIM_SIZE) + offset;

      const tooltipRect: ClientRect = popup.getBoundingClientRect();
      const parentRect: ClientRect = parent.getBoundingClientRect();

      popup.style.left = `${parentRect.left}px`;
      popup.style.top = `${parentRect.top - tooltipRect.height - additionalOffset}px`;
      popup.style.bottom = 'inherit';
    },

    topLeft(positionData: GasPositioning.PositionSetup): void {
      const { popup, parent, offset, arrowWidth } = positionData;
      const additionalOffset = (arrowWidth / 2 + TRIM_SIZE) + offset;

      const tooltipRect: ClientRect = popup.getBoundingClientRect();
      const parentRect: ClientRect = parent.getBoundingClientRect();

      popup.style.left = `${parentRect.right - tooltipRect.width}px`;
      popup.style.top = `${parentRect.top - tooltipRect.height - additionalOffset}px`;
      popup.style.bottom = 'inherit';
    },

    right(positionData: GasPositioning.PositionSetup): void {
      const { popup, parent, offset, arrowWidth } = positionData;
      const additionalOffset = (arrowWidth / 2 + TRIM_SIZE) + offset;

      const parentRect: ClientRect = parent.getBoundingClientRect();

      popup.style.left = `${parentRect.right + additionalOffset}px`;
      initTopPositionForRightLeftCase(positionData);
    },

    left(positionData: GasPositioning.PositionSetup): void {
      const { popup, offset, parent, arrowWidth } = positionData;
      const additionalOffset = (arrowWidth / 2 + TRIM_SIZE) + offset;

      const tooltipRect: ClientRect = popup.getBoundingClientRect();
      const parentRect: ClientRect = parent.getBoundingClientRect();

      popup.style.left = `${parentRect.left - tooltipRect.width - additionalOffset}px`;
      initTopPositionForRightLeftCase(positionData);
    },
  };
}
